/**
 * PermissionGrid Component
 *
 * Reusable visual grid for displaying action x subject permission matrices.
 * Used by PermissionMatrix (authz groups) and AgentDefinitionPermissionsTab (tool-derived).
 */

import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';

// --- Types ---

export type GridAction = {
  key: string;
  label: string;
};

export type GridCell = {
  actionKey: string;
  checked: boolean;
  /** When true, the action is not valid for this row — render "—" */
  invalid?: boolean;
  /** Lines shown in a popover on click (e.g. tool names that grant this permission) */
  popoverLines?: string[];
};

export type GridRow = {
  key: string;
  label: string;
  cells: GridCell[];
};

export type GridSection = {
  key: string;
  label: string;
  rows: GridRow[];
};

export type GridGroup = {
  key: string;
  label: string;
  sections: GridSection[];
};

export type PermissionGridProps = {
  actions: GridAction[];
  groups: GridGroup[];
  /** Called when a checkbox is toggled. Omit for read-only grids. */
  onToggle?: (rowKey: string, actionKey: string) => void;
  /** Limit toggling to already-checked cells (remove-only mode). */
  checkedOnlyToggle?: boolean;
  /** Called when the All/None button is clicked for a row. Omit to hide toggle-all. */
  onToggleRow?: (rowKey: string) => void;
  /** Disable all checkboxes */
  disabled?: boolean;
  /**
   * When true (default), resources are sorted A–Z by domain, section, and row label,
   * and a search field filters rows by resource name, enum key, domain, or section.
   */
  resourceSearchable?: boolean;
};

// --- Helpers ---

function countRowEnabled(row: GridRow): number {
  return row.cells.filter((c) => c.checked && !c.invalid).length;
}

function isRowAllEnabled(row: GridRow): boolean {
  const validCells = row.cells.filter((c) => !c.invalid);
  return validCells.length > 0 && validCells.every((c) => c.checked);
}

function sortGridGroupsAlphabetically(groups: GridGroup[]): GridGroup[] {
  return [...groups]
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )
    .map((group) => ({
      ...group,
      sections: [...group.sections]
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
        )
        .map((section) => ({
          ...section,
          rows: [...section.rows].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
          ),
        })),
    }));
}

function filterGridGroupsBySearch(
  groups: GridGroup[],
  query: string,
): GridGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return groups;
  }
  return groups
    .map((group) => ({
      ...group,
      sections: group.sections
        .map((section) => ({
          ...section,
          rows: section.rows.filter((row) => {
            const blob =
              `${row.label} ${row.key} ${group.label} ${section.label}`.toLowerCase();
            return blob.includes(q);
          }),
        }))
        .filter((s) => s.rows.length > 0),
    }))
    .filter((g) => g.sections.length > 0);
}

// --- Component ---

export function PermissionGrid({
  actions,
  groups,
  onToggle,
  checkedOnlyToggle = false,
  onToggleRow,
  disabled = false,
  resourceSearchable = true,
}: PermissionGridProps) {
  const gt = useGT();
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _isReadOnly = !onToggle;
  const [searchQuery, setSearchQuery] = useState('');

  const displayGroups = useMemo(() => {
    const sorted = sortGridGroupsAlphabetically(groups);
    if (!resourceSearchable) {
      return sorted;
    }
    return filterGridGroupsBySearch(sorted, searchQuery);
  }, [groups, resourceSearchable, searchQuery]);

  return (
    <div className="w-full space-y-3">
      {resourceSearchable && groups.length > 0 && (
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={gt('Search resources...')}
            className="h-9 pl-8"
            aria-label={gt('Search permissions by resource name')}
          />
        </div>
      )}

      {displayGroups.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          {searchQuery.trim() ? (
            <T>No resources match your search.</T>
          ) : (
            <T>No permissions to display.</T>
          )}
        </p>
      ) : (
        <Accordion
          type="multiple"
          defaultValue={displayGroups.map((g) => g.key)}
          className="w-full"
        >
          {displayGroups.map((group) => {
            const totalResources = group.sections.reduce(
              (acc, s) => acc + s.rows.length,
              0,
            );

            return (
              <AccordionItem
                key={group.key}
                value={group.key}
                className="border rounded-lg mb-2 px-3 data-[open]:bg-muted/30"
              >
                <AccordionTrigger className="py-3 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{group.label}</span>
                    <Badge
                      variant="outline"
                      className="text-xs h-5 font-normal"
                    >
                      {totalResources === 1
                        ? gt('1 resource', {
                            context: 'permission grid resource count',
                          })
                        : gt('{count} resources', {
                            context: 'permission grid resource count',
                            count: String(totalResources),
                          })}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <div className="space-y-6">
                    {group.sections.map((section) => (
                      <div key={section.key}>
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                          {section.label}
                        </h4>

                        <div className="overflow-x-auto rounded-lg border bg-card">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-muted/50">
                                <th className="text-left py-2.5 px-3 font-medium text-muted-foreground w-[200px]">
                                  <T>Resource</T>
                                </th>
                                {actions.map((action) => (
                                  <th
                                    key={action.key}
                                    className="px-3 py-2.5 text-center font-medium text-muted-foreground w-[80px]"
                                  >
                                    {action.label}
                                  </th>
                                ))}
                                {onToggleRow && (
                                  <th className="w-[60px]">
                                    <span className="sr-only">
                                      <T context="permission grid column, toggle every action in a row">
                                        Toggle all
                                      </T>
                                    </span>
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {section.rows.map((row, idx) => {
                                const enabledCount = countRowEnabled(row);
                                const allEnabled = isRowAllEnabled(row);

                                return (
                                  <tr
                                    key={row.key}
                                    className={cn(
                                      'group transition-colors',
                                      idx !== section.rows.length - 1 &&
                                        'border-b',
                                      'hover:bg-muted/30',
                                    )}
                                  >
                                    <td className="py-2.5 px-3">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">
                                          {row.label}
                                        </span>
                                        {enabledCount > 0 && (
                                          <Badge
                                            variant="secondary"
                                            className="text-[10px] h-4 px-1.5"
                                          >
                                            {enabledCount}
                                          </Badge>
                                        )}
                                      </div>
                                    </td>
                                    {row.cells.map((cell) => {
                                      if (cell.invalid) {
                                        return (
                                          <td
                                            key={cell.actionKey}
                                            className="px-3 py-2.5 text-center"
                                          >
                                            <div className="flex justify-center">
                                              <span className="text-muted-foreground/30">
                                                —
                                              </span>
                                            </div>
                                          </td>
                                        );
                                      }

                                      const canToggle =
                                        !!onToggle &&
                                        (!checkedOnlyToggle || cell.checked);
                                      const checkboxEl = (
                                        <Checkbox
                                          checked={cell.checked}
                                          onCheckedChange={
                                            canToggle
                                              ? () =>
                                                  onToggle(
                                                    row.key,
                                                    cell.actionKey,
                                                  )
                                              : undefined
                                          }
                                          disabled={disabled || !canToggle}
                                          className={cn(
                                            'transition-all',
                                            cell.checked &&
                                              canToggle &&
                                              'bg-primary border-primary data-[checked]:bg-primary',
                                            cell.checked &&
                                              !canToggle &&
                                              'bg-primary/40 border-primary/40 data-[checked]:bg-primary/40 opacity-100 disabled:opacity-100',
                                            !cell.checked &&
                                              'opacity-100 disabled:opacity-100',
                                          )}
                                        />
                                      );

                                      return (
                                        <td
                                          key={cell.actionKey}
                                          className="px-3 py-2.5 text-center"
                                        >
                                          <div className="flex justify-center">
                                            {cell.popoverLines?.length ? (
                                              <Popover>
                                                <PopoverTrigger asChild>
                                                  <div className="inline-flex cursor-pointer">
                                                    {checkboxEl}
                                                  </div>
                                                </PopoverTrigger>
                                                <PopoverContent
                                                  side="top"
                                                  className="w-auto max-w-xs p-3"
                                                >
                                                  <p className="text-xs font-medium text-muted-foreground mb-1.5">
                                                    <T>Granted by:</T>
                                                  </p>
                                                  <div className="space-y-1">
                                                    {cell.popoverLines.map(
                                                      (line, lineIdx) => (
                                                        <p
                                                          key={`${line}-${lineIdx}`}
                                                          className="text-xs"
                                                        >
                                                          {line}
                                                        </p>
                                                      ),
                                                    )}
                                                  </div>
                                                </PopoverContent>
                                              </Popover>
                                            ) : (
                                              checkboxEl
                                            )}
                                          </div>
                                        </td>
                                      );
                                    })}
                                    {onToggleRow && (
                                      <td className="px-2 py-2.5">
                                        {row.cells.some((c) => !c.invalid) && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={() => onToggleRow(row.key)}
                                            disabled={disabled}
                                          >
                                            {allEnabled ? (
                                              <T>None</T>
                                            ) : (
                                              <T>All</T>
                                            )}
                                          </Button>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
}
